<script setup lang="ts">
import type { ComputedRef } from 'vue'
import type { MediaLoadingStrategy } from 'vidstack'
import type { ArticleContentTypes, ContentArticle } from '~/models/Content/ContentArticle'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { computed, onBeforeUnmount, onMounted, provide, useTemplateRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { setTitle } from '~/utils/dom'
import useLicenseControlStore from '~/stores/licenseControl'
import useAuthStore from '~/stores/auth'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import { ArticleContentTypeCriterion } from '~/models/Content/ContentArticle'
import { ColorTheme } from '~/models/Content/Color'
import useSlugify from '~/composables/useSlugify'
import { useScrollBehavior } from '~/composables/useScrollBehavior'
import { useOverlayOutlet } from '~/composables/useOverlayOutlet.js'
import { useContentHelper } from '~/composables/useContentHelper'
import { useColorTheme } from '~/composables/useColorTheme'
import { useAppColor } from '~/composables/useAppColor'
import { useContentEvent } from '~/composables/activity/useContentEvent'
import useContentApi from '~/api/contentApi'
import TopBar from '~/components/utils/TopBar.vue'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import AuSpinner from '~/components/ui/AuSpinner.vue'
import ArticleContentSkeleton from '~/components/skeletons/ArticleContentSkeleton.vue'
import NotFound from '~/components/ResourceEngine/Components/NotFound.vue'
import ArticleWordlist from '~/components/article/ArticleWordlist.vue'
import ArticleNavigation from '~/components/article/ArticleNavigation.vue'
import ArticleHeaderRenderer from '~/components/article/ArticleHeaderRenderer.vue'
import ArticleContentRenderer from '~/components/article/ArticleContentRenderer.vue'
import ArticleColumnRenderer from '~/components/article/ArticleColumnRenderer.vue'

const props = defineProps<{
  locationId: number
  slug: string
  subLocationId?: number
}>()

useScrollBehavior()
const overlayOutlet = useOverlayOutlet(useTemplateRef('overlay-outlet'))

provide('embedAudioVariant', 'inline')
provide<MediaLoadingStrategy>('embedAudioLoad', 'visible')
provide<ComputedRef<boolean>>('isTiered', computed(() => currentArticle.value?.tiered ?? false))
provide<ComputedRef<number>>('versionNo', computed(() => currentArticle.value?.versionNo ?? 0))

const router = useRouter()
const { t } = useI18n()
const { findContents } = useContentApi()
const { checkItemLicense } = useLicenseControlStore()
const { set: setAppColor, clear: clearAppColor } = useAppColor()
const { slugify } = useSlugify()
const { getTitle } = useContentHelper()
const { isAuthenticated } = storeToRefs(useAuthStore())

const { data: article, isLoading, isError } = useQuery({
  staleTime: Infinity,
  queryKey: computed(() => ['app-article', props.locationId]),
  queryFn: () => findContents<ContentArticle>({
    locationIdCriterion: [props.locationId],
    contentTypeCriterion: [ContentType.Article],
  }, 1).then(([data]) => data),
  retry: 1,
})

const { autoContentEvent } = useContentEvent()
autoContentEvent(article)

const subArticleIds = computed(() => article.value?.articles?.destinationContentIds ?? [])
const hasSubArticles = computed(() => subArticleIds.value.length > 0)

const { data: subArticles, isLoading: isLoadingSubArticles } = useQuery({
  staleTime: Infinity,
  queryKey: computed(() => ['app-article-subs', props.locationId]),
  queryFn: () => findContents<ContentArticle>({
    contentIdCriterion: subArticleIds.value,
    contentTypeCriterion: [ContentType.Article],
    mainLocationCriterion: true,
    subtreeCriterion: [Subtree.Content, Subtree.Media],
  }, subArticleIds.value.length),
  select: articles => articles
    .sort((a, b) => subArticleIds.value.indexOf(a.contentId) - subArticleIds.value.indexOf(b.contentId)),
  retry: 1,
  enabled: hasSubArticles
})

const currentArticle = computed(() => {
  if (isLoading.value || isLoadingSubArticles.value) {
    return undefined
  }
  return props.subLocationId || hasSubArticles.value
    ? subArticles.value?.find(({ locationId }) => locationId === props.subLocationId)
    : article.value
})

const currentContents = computed(() => currentArticle?.value?.content?.destinationContentIds ?? [])
const currentAsides = computed(() => currentArticle?.value?.asideContent?.destinationContentIds ?? [])

const contentLimit = computed(() => isAuthenticated.value ? currentContents.value.length : 1)
const asideLimit = computed(() => isAuthenticated.value ? currentAsides.value.length : 1)

const {
  data: sections,
  isLoading: isLoadingSections,
  isError: sectionsError
} = useQuery({
  staleTime: Infinity,
  queryKey: computed(() => ['app-article-content', ...currentContents.value]),
  queryFn: () => findContents<ArticleContentTypes>({
    contentIdCriterion: currentContents.value,
    contentTypeCriterion: ArticleContentTypeCriterion,
    mainLocationCriterion: true,
    subtreeCriterion: [Subtree.Content, Subtree.Media],
  }, contentLimit.value),
  select: contents => contents
    .sort((a, b) => currentContents.value.indexOf(a.contentId) - currentContents.value.indexOf(b.contentId)),
  retry: 1,
  enabled: computed(() => currentContents.value.length > 0),
})

const hasSequenceSection = computed(() => sections.value?.some(section =>
  section.contentTypeIdentifier === ContentType.Sequence) ?? false)

const { data: asideContent } = useQuery({
  staleTime: Infinity,
  queryKey: computed(() => ['app-article-aside-content', ...currentAsides.value]),
  queryFn: () => findContents<ArticleContentTypes>({
    contentIdCriterion: currentAsides.value,
    contentTypeCriterion: ArticleContentTypeCriterion,
    mainLocationCriterion: true,
    subtreeCriterion: [Subtree.Content, Subtree.Media],
  }, asideLimit.value),
  select: asides => asides
    .sort((a, b) => currentAsides.value.indexOf(a.contentId) - currentAsides.value.indexOf(b.contentId)),
  enabled: computed(() => currentAsides.value.length > 0),
  retry: 1,
})

const colorHeader = computed(() => currentArticle.value?.headerVariation?.identifier === 'color')
const leftColumnHeader = computed(() => currentArticle.value?.headerVariation?.identifier === 'left_column')
const themeIdentifier = computed(() => currentArticle.value?.colorTheme?.identifier ?? ColorTheme.BlueOrange)
const { theme, style } = useColorTheme(themeIdentifier)

const size = computed(() => currentArticle.value?.size.identifier ?? 'medium')

const fontSize = computed(() => {
  switch (size.value) {
    case 'medium': return 1.2
    case 'large': return 1.375
    case 'xlarge': return 2
    default: return 1.2
  }
})

watch(subArticles, (articles) => {
  if (!props.subLocationId && articles && articles.length > 0) {
    const subArticle = articles.length > 1 ? articles[1] : articles[0]
    let path = router.resolve({ name: 'article_sub', params: {
      locationId: props.locationId,
      slug: props.slug,
      subLocationId: subArticle.locationId,
      subSlug: slugify(subArticle.title),
    } }).href
    if (router.currentRoute.value.params?.listId) {
      path = router.resolve({
        name: 'favorite_list_navigation',
        params: { listId: router.currentRoute.value.params.listId }
      }).href + path
    }
    router.replace(path)
  }
}, { immediate: true })

watch(currentArticle, () => {
  if (currentArticle.value)
    setTitle(getTitle(currentArticle.value))
}, { immediate: true })

watch(article, () => {
  if (article.value)
    checkItemLicense(article.value)
}, { immediate: true })

watch(theme, (theme) => {
  setAppColor(theme[5])
}, { immediate: true })

onMounted(() => setAppColor(theme.value[5]))
onBeforeUnmount(() => clearAppColor())

provide('theme', theme)
</script>

<template>
  <TopBar
    variant="colorPair"
    :item="currentArticle"
    :color-pair="{
      background: theme[5],
      text: theme[50],
      isDark: false,
    }"
  />
  <AuSpinner
    v-if="isLoading || isLoadingSubArticles"
    center
  />
  <NotFound v-else-if="isError" />
  <article
    v-else
    :style
    class="au-article grid min-h-auto auto-rows-max gap-x-(--col-gap) bg-(--theme-5) text-(--theme-60)"
    :class="{
      'mb-[calc(var(--col-width)+2rem)]': !leftColumnHeader && !asideContent?.length,
      'gap-y-(--col-width)': !leftColumnHeader,
      'relative !mb-0 snap-y snap-mandatory *:snap-start *:snap-always': hasSequenceSection,
    }"
  >
    <template v-if="currentArticle && !leftColumnHeader">
      <ArticleHeaderRenderer
        :article="currentArticle"
        :sub-articles="subArticles"
        :route-params="{ locationId, slug }"
        :has-sequence-section
      />
      <ArticleWordlist
        v-if="currentArticle.wordlist.length"
        :item="currentArticle"
        :size
      />
    </template>
    <div v-if="currentArticle?.body && !asideContent?.length" class="au-prose col-[text]">
      <RichTextRenderer :text="currentArticle.body" />
    </div>
    <ArticleContentSkeleton
      v-if="isLoadingSections"
      :color="theme[10]"
    />
    <p
      v-else-if="sectionsError"
      class="col-[text]"
      v-text="t('article.error')"
    />
    <template v-else-if="sections?.length || asideContent?.length">
      <ArticleColumnRenderer
        v-if="currentArticle && asideContent?.length"
        :open="currentArticle.asideOpen"
        :article="currentArticle"
        :main-content="sections"
        :aside-content="asideContent"
        :sub-articles="subArticles ? subArticles : []"
        :route-params="{ locationId, slug }"
        :has-sequence-section
        :size
        :class="{ 'mt-[calc(var(--col-width)*-1)]': (currentArticle.wordlist.length === 0) && colorHeader }"
      >
        <template #default="{ items, aside }">
          <ArticleContentRenderer
            :location-id
            :sections="items"
            :size
            :has-sequence-section="!aside && hasSequenceSection"
          />
        </template>
      </ArticleColumnRenderer>
      <ArticleContentRenderer
        v-else-if="sections?.length"
        :location-id
        :sections
        :size
        :has-sequence-section
      />
    </template>
  </article>
  <div
    :id="overlayOutlet.id"
    ref="overlay-outlet"
    class="sticky bottom-0 z-[100] bg-(--theme-60) text-(--theme-10) [&:not(:empty)]:p-4"
    :style="{
      ...style,
      '--media-color': 'var(--theme-10)',
      '--media-bg-color': 'var(--theme-60)',
    }"
  />
  <!--  Navigation  -->
  <ArticleNavigation
    v-if="article"
    :class="{ 'snap-start': hasSequenceSection }"
    :item="article"
    :style
  />
</template>

<style scoped>
@reference "tailwindcss";

.au-article {
  font-size: calc(1rem * v-bind(fontSize));
  --col-width: 2rem;
  --col-gap: 1rem;

  grid-template-columns:
    [fill-start] minmax(0, 1fr)
    [media-start extext-start text-start] minmax(0, 30ch)
    [midpoint]
    minmax(0, 30ch) [text-end media-end extext-end]
    minmax(0, 1fr) [fill-end];

  @media (min-width: 768px) {
    grid-template-columns:
      [fill-start] minmax(var(--col-gap), 1fr)
      [media-start] minmax(0, var(--col-width))
      [extext-start] minmax(0, var(--col-width))
      [text-start] minmax(0, 30ch)
      [midpoint]
      minmax(0, 30ch) [text-end]
      minmax(0, var(--col-width)) [extext-end]
      minmax(0, var(--col-width)) [media-end]
      minmax(var(--col-gap), 1fr) [fill-end];
  }

  @media (min-width: 1024px) {
    --col-width: 4rem;
    --col-gap: 2rem;
  }

  & :deep(.au-subtitle),
  & :deep(.cms-h3) {
    font-size: 1.4em;
  }

  & :deep(.au-bigtext) {
    font-size: 2em;
  }

  & :deep(.cms-h) {
    font-weight: 700;

    &.cms-h2 {
      font-size: 1.6em;
    }

    &.cms-h4 {
      font-size: 1.2em;
    }

    &:is(.cms-h5, .cms-h6) {
      font-size: 1em;
    }
  }

  & :deep(.au-title) {
    font-size: 2.8em;
    line-height: 1.25;
  }

  & :deep(.au-prose) {
    word-break: break-word;
  }

  & :deep(.au-prose > :not(:where(:first-child), .cms-ol, .cms-ul)),
  & :deep(.au-prose > .ibexa-embed-type-image),
  & :deep(.au-prose .cms-li) {
    margin-block-start: 1em;
  }

  & :deep(.cms-a) {
    color: inherit;
    text-underline-offset: 4px;
    text-decoration-line: underline;

    &:hover {
      background-color: hsl(from currentColor h s l / 0.15);
    }

    &:focus-visible {
      box-shadow: var(--tw-ring-inset,) 0 0 0 calc(3px + var(--tw-ring-offset-width))
        var(--tw-ring-color, var(--color-blue-30));
    }
  }

  & :deep(.au-prose :where(.cms-ul, .cms-ol)) {
    /* Prevents the box from overlapping with floating images. This fixes bullet points being rendered on-top/behind images */
    overflow: hidden;
    padding-inline-start: 3ch;

    & li {
      padding-inline-start: 0.5ch;
    }
  }

  & :deep(.au-prose .cms-ul) {
    /* NOTE: Safari doesn't support `content` on `::marker` and falls back to list-style */
    list-style-type: disc;

    & li::marker {
      content: '•  ';
    }
  }

  & :deep(.au-prose .cms-ol) {
    list-style-type: decimal;

    &.upper-latin {
      list-style-type: upper-latin;
    }

    &.lower-latin {
      list-style-type: lower-latin;
    }

    &.upper-roman {
      list-style-type: upper-roman;
    }

    &.lower-roman {
      list-style-type: lower-roman;
    }

    &.decimal-leading-zero {
      list-style-type: decimal-leading-zero;
    }
  }

  & :deep(.au-prose .cms-table) {
    border-collapse: separate;
    border-spacing: 0;
    border: 1px solid var(--theme-10);
    position: relative;
    border-radius: var(--radius-xl);
    overflow: clip;
    width: 100%;

    tr:nth-child(odd) {
      background: var(--color-white);
    }

    th {
      background: var(--theme-10);
      text-align: left;
    }

    :where(td, th) {
      border-left: 1px solid var(--theme-50);
      border-top: 1px solid var(--theme-50);
      padding: 1rem;
      width: 0;

      &:first-child {
        border-left: none;
      }
    }

    thead tr:first-child :where(td, th) {
      border-top: none;
    }
  }

  & :deep(.cms-blockquote),
  & :deep(.quoteauthor-textfield) {
    margin-block-end: var(--col-width);
  }

  & :deep(.cms-blockquote),
  & :deep(.quoteauthor-textfield blockquote) {
    margin-block-start: var(--col-width);
    font-family: tiempos, serif;
    font-size: 2em;
    position: relative;

    :deep(.cms-p) {
      display: inline;
      padding-inline: 1rem;
      box-decoration-break: clone;
      background-color: var(--theme-10);
    }

    &::before {
      display: block;
      content: '';
      mask-image: url('/quote.svg');
      mask-size: 100%;
      mask-repeat: no-repeat;
      width: 4rem;
      height: 4rem;
      background-color: var(--theme-50);

      @media (min-width: 768px) {
        position: absolute;
        right: calc(100% + 1rem);
        top: -1rem;
      }
    }
  }

  & :deep(.quoteauthor-textfield blockquote ~ *) {
    display: inline-block;
    margin-top: 1rem;
    padding-inline: 1rem;
    background-color: var(--theme-10);
  }

  & :deep(.au-color-box) {
    padding: var(--col-gap);
    gap: var(--col-gap);

    @media (width >= 48rem) {
      margin-inline: calc(var(--col-gap) * -1);
    }
  }

  & :deep(.au-prose .au-flyt) {
    @apply mx-[calc(var(--col-gap)*-1)] rounded-xl p-(--col-gap) bg-[#ffead1ff];
  }

  & :deep(.au-prose:has(.au-image-collection-embed)) {
    overflow: visible;
  }
}
</style>
